import { useState } from 'react'
import { useTranslate, useRecordContext, Form, useNotify, useRefresh } from 'react-admin'
import {
  Box,
  Typography,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  DialogTitle,
  Dialog,
} from '@mui/material'
import { DatePickers } from 'src/components/uiElements/react-hook-fields'
import EventIcon from '@mui/icons-material/Event'
import { blue } from 'src/assets/colors'
import { getDay } from '../../helper/getDay'
import LoadingButton from '@mui/lab/LoadingButton'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { HasPermission, hasPermissionFunction } from 'src/helper/HasPermission'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { PERMISSION_EDIT_DUE_DATE } from 'src/helper/Permission'
import dayjs from 'dayjs'
import { itemServices } from 'src/services/reservation/item/item.service'
import { customAwait } from '../../helper/customAwait'
import { useReservationInfo } from '../../context/ReservationContext'
import { GUARANTEED } from './state'


const ModalEditDueDateFinal = ({ openModal, onCloseModal, due_date_final, due_date, item_hash }) => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const notify = useNotify()
  const record = useRecordContext()
  const [loadingButton, setLoadingButton] = useState(false)


  const dueDate = dayjs(due_date)
  const createdDate = dayjs(record.created_at)
  const minDate = dueDate.isValid() ? createdDate : null // created at
  const maxDate = dueDate.isValid() ? dueDate.add(5, 'day') : null // 5 días después

  const postSave = async (data) => {
    try {
      setLoadingButton(true)
      const dueDate = dayjs(data?.due_date_final)
      if (!dueDate.isValid()) {
        notify('ra.notification.due_date_error', {
          type: 'error',
        })
        return
      }
      const formattedDate = dueDate.format('YYYY-MM-DD') 
      const status = await itemServices.update_due_date(item_hash, formattedDate)
      if (status >= 200 && status <= 204) {
        notify('ra.notification.due_date_success', {
          type: 'success',
        })
      } else {
        notify('ra.notification.due_date_error', {
          type: 'error',
        })
      }
    } catch (error) {
      notify('ra.notification.due_date_error', {
        type: 'error',
      })
    } finally {
      customAwait([refresh, () =>  setLoadingButton(false), () => onCloseModal()])
    }
  }
  return (
    <Dialog fullWidth open={openModal} onClose={onCloseModal} maxWidth='xs'>
      <DialogTitle>{translate('ra.title.update_due_date')}</DialogTitle>
      <DialogContent>
        <Form
          id='post_edit_due_date_form'
          warnWhenUnsavedChanges
          onSubmit={postSave}
          record={{ due_date_final: due_date_final }}
        >
          <Grid container spacing={3}>
            <EditInputsWrap label={translate('ra.field.date')} lg={12} md={12}>
              {hasPermissionFunction('item', 'full_edit_due_date') ?
              <DatePickers
                source='due_date_final'
                viewsShow={false}
              />
              :
              <DatePickers
                source='due_date_final'
                viewsShow={false}
                minDate={minDate || null}
                maxDate={maxDate || null}
              />
              }
            </EditInputsWrap>
          </Grid>
        </Form>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onCloseModal}>
          {translate('ra.action.close')}
        </Button>
        <LoadingButton
          loading={loadingButton}
          variant='contained'
          startIcon={<SaveIcon />}
          onClick={(e) => {
            e.stopPropagation()
          }}
          form='post_edit_due_date_form'
          type='submit'
        >
          {translate('ra.action.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

const Cancellations = ({ title, date, due_date, is_due_date = false, item_hash = null, payment_status = null }) => {
  const { openDueDateModal, setOpenDueDateModal } = useReservationInfo()

  const getStatus = (date) => {
    try {
      const dueDate = new Date(date + 'T00:00:00')
      dueDate.setHours(0, 0, 0, 0)
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const diffTime = dueDate.getTime() - today.getTime()
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      let color = null
      if (diffDays === 0) {
        color = '#FF9800' // Vence hoy
      } else if (diffDays < 0) {
        color = '#f44336' // Fecha ya vencida
      }
      return color
    } catch (error) {
      return null
    }
  }

  const isNotGuaraanted = (payment_status) => {
    try {
      if (hasPermissionFunction('item', 'full_edit_due_date')){ // si tiene permiso especial, siempre puede editar el vencimiento
        return false
      }

      if (payment_status === GUARANTEED){
        return false
      }
      return true
    } catch (error) {
      return true
    }
  }

  return (
    <Box style={{ padding: '10px' }}>
      <Box className='cancellation_container'>
        <Box className='cancellation_column'>
          <Typography variant='h7' component='div' fontWeight={600}>
            {title}:
          </Typography>
          <Box display={'flex'}>
            <Typography
              variant='h7'
              component='div'
              style={{
                border: '1px  solid lightgray',
                padding: '3px',
                borderRadius: '10px',
                color: getStatus(date),
                fontWeight: 600,
                display: 'flex',
              }}
            >
              <EventIcon /> {getDay(date)}
            </Typography>
            {is_due_date && (
              <>
                <HasPermission permission={PERMISSION_EDIT_DUE_DATE} resource={'item'}>
                  <LoadingButton
                    size='small'
                    onClick={() => setOpenDueDateModal(true)}
                    startIcon={<EditIcon fontSize='small' />}
                    disabled={isNotGuaraanted(payment_status)}
                    color='primary'
                    sx={{ marginLeft: '10px' }}
                  ></LoadingButton>
                </HasPermission>
                {openDueDateModal && (
                  <ModalEditDueDateFinal
                    openModal={openDueDateModal}
                    onCloseModal={() => setOpenDueDateModal(false)}
                    due_date_final={date}
                    due_date={due_date}
                    item_hash={item_hash}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export const DueDateOfProduct = () => {
  const record = useRecordContext()
  const translate = useTranslate()

  const areDifferent = (record) => {
    try {
      const dueDateFinal = new Date(record?.due_date_final + 'T00:00:00')
      dueDateFinal.setHours(0, 0, 0, 0)
      const dueDate = new Date(record?.due_date + 'T00:00:00')
      dueDate.setHours(0, 0, 0, 0)

      const diffTime = dueDate.getTime() - dueDateFinal.getTime()
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays !== 0) {
        return true
      }
      return false
    } catch (error) {
      return true
    }
  }

  if (record?.due_date_final && record?.due_date) {
    return (
      <Box>
        <Typography variant='h6' component='div' color={blue} margin={'25px 0px 10px 0px'}>
          {translate('ra.field.due_date')}
        </Typography>
        {areDifferent(record) ? (
          <>
            <Cancellations
              title={translate('ra.field.date')}
              date={record?.due_date}
              due_date={record?.due_date}
              payment_status={record?.payment_status}
            />
            <Cancellations
              title={translate('ra.field.due_date_final')}
              date={record?.due_date_final}
              item_hash={record?.hash_}
              due_date={record?.due_date}
              is_due_date={true}
              payment_status={record?.payment_status}
            />
          </>
        ) : (
          <Cancellations
            title={translate('ra.field.date')}
            date={record?.due_date_final}
            item_hash={record?.hash_}
            due_date={record?.due_date}
            is_due_date={true}
            payment_status={record?.payment_status}
          />
        )}
      </Box>
    )
  }
}
